import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack as ChakraStack,
  Text,
  Textarea,
  Divider,
} from '@chakra-ui/react'
import type { TFunction } from 'i18next'

interface StatusUpdateModalProps {
  isOpen: boolean
  onClose: () => void
  currentStatusLabel: string
  targetStatus: 'compliance_hold' | 'closed'
  onTargetStatusChange: (value: 'compliance_hold' | 'closed') => void
  auditLog: string
  notesToPublic: string
  closeContract: boolean
  isCloseContractDisabled: boolean
  closeContractDisabledReasonKey?: string
  onChangeAuditLog: (value: string) => void
  onChangeNotesToPublic: (value: string) => void
  onChangeCloseContract: (value: boolean) => void
  onConfirm: () => void
  isSubmitting: boolean
  t: TFunction
  textColor: string
}

export const isOrderPauseControlDisabled = (
  _targetStatus: 'compliance_hold' | 'closed',
  isCapabilityDisabled: boolean
): boolean => isCapabilityDisabled

export const StatusUpdateModal = ({
  isOpen,
  onClose,
  currentStatusLabel,
  targetStatus,
  onTargetStatusChange,
  auditLog,
  notesToPublic,
  closeContract,
  isCloseContractDisabled,
  closeContractDisabledReasonKey,
  onChangeAuditLog,
  onChangeNotesToPublic,
  onChangeCloseContract,
  onConfirm,
  isSubmitting,
  t,
  textColor,
}: StatusUpdateModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('orders.actions.onholdOrCloseTitle')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <ChakraStack spacing={3}>
            <FormControl>
              <ChakraStack direction="row" align="center" justify="space-between">
                <FormLabel fontSize="sm" mb={0}>
                  {t('orders.actions.currentStatus')}
                </FormLabel>
                <Text fontSize="sm" color={textColor}>
                  {currentStatusLabel}
                </Text>
              </ChakraStack>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">{t('orders.actions.targetStatus')}</FormLabel>
              <RadioGroup
                value={targetStatus}
                onChange={(value) => {
                  const next = value as 'compliance_hold' | 'closed'
                  onTargetStatusChange(next)
                }}
              >
                <ChakraStack direction="row" spacing={6}>
                  <Radio value="compliance_hold">{t('orders.actions.targetComplianceHold')}</Radio>
                  <Radio value="closed">{t('orders.actions.targetClosed')}</Radio>
                </ChakraStack>
              </RadioGroup>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">{t('orders.actions.auditLog')}</FormLabel>
              <Textarea
                value={auditLog}
                onChange={(event) => onChangeAuditLog(event.target.value)}
                placeholder={t('orders.actions.auditLogPlaceholder')}
                rows={3}
              />
              <FormHelperText>{t('orders.actions.auditLogHelper')}</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">{t('orders.actions.notesToPublic')}</FormLabel>
              <Textarea
                value={notesToPublic}
                onChange={(event) => onChangeNotesToPublic(event.target.value)}
                placeholder={t('orders.actions.notesToPublicPlaceholder')}
                rows={3}
              />
            </FormControl>

            <Divider />

            <FormControl>
              <Checkbox
                isChecked={closeContract}
                onChange={(event) => onChangeCloseContract(event.target.checked)}
                isDisabled={isOrderPauseControlDisabled(
                  targetStatus,
                  isCloseContractDisabled
                )}
              >
                {t('orders.actions.closeContract')}
              </Checkbox>
              <FormHelperText>
                {t(closeContractDisabledReasonKey ?? 'orders.actions.closeContractHint')}
              </FormHelperText>
            </FormControl>
          </ChakraStack>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="gray" mr={3} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="red"
            onClick={onConfirm}
            isLoading={isSubmitting}
          >
            {t('orders.actions.confirmStatusChange')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
